import type { PlayerId } from "@cloudnest/warchest-engine";
import type { ReactElement } from "react";

export const PLAYER_COLORS: Record<PlayerId, string> = {
  P1: "var(--cn-warchest-raven)",
  P2: "var(--cn-warchest-wolf)",
};

export const PLAYER_DARK_COLORS: Record<PlayerId, string> = {
  P1: "var(--cn-warchest-coin-rim-stroke)",
  P2: "var(--cn-warchest-coin-rim-stroke)",
};

type SymbolRenderer = (props: { color: string }) => ReactElement;

const SYMBOLS: Record<string, SymbolRenderer> = {
  ARCHER: ({ color }) => (
    <>
      <path
        d="M4,16 Q10,4 16,16"
        fill="none"
        stroke={color}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <line x1="4" y1="16" x2="16" y2="16" stroke={color} strokeWidth={1.5} />
      <line
        x1="10"
        y1="4"
        x2="10"
        y2="16"
        stroke={color}
        strokeWidth={1}
        strokeDasharray="2,1.5"
      />
    </>
  ),
  BERSERKER: ({ color }) => (
    <path
      d="M7,18 C6,12 4,7 7,6 C10,6 10,11 10,11 C10,11 10,6 13,6 C16,7 14,12 13,18"
      fill={color}
      stroke={color}
      strokeWidth={0.5}
    />
  ),
  CAVALRY: ({ color }) => (
    <path
      d="M3,6 L9,10 L3,14 M10,6 L16,10 L10,14"
      fill="none"
      stroke={color}
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  CROSSBOWMAN: ({ color }) => (
    <>
      <line
        x1="10"
        y1="3"
        x2="10"
        y2="17"
        stroke={color}
        strokeWidth={3}
        strokeLinecap="round"
      />
      <line
        x1="3"
        y1="9"
        x2="17"
        y2="9"
        stroke={color}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
    </>
  ),
  ENSIGN: ({ color }) => (
    <>
      <line
        x1="6"
        y1="3"
        x2="6"
        y2="17"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
      />
      <polygon points="6,3 15,7 6,11" fill={color} />
    </>
  ),
  FOOTMAN: ({ color }) => (
    <path
      d="M5,4 L15,4 L15,12 Q10,19 5,12 Z"
      fill={color}
      opacity={0.75}
      stroke={color}
      strokeWidth={1}
    />
  ),
  KNIGHT: ({ color }) => (
    <polygon
      points="10,3 17,10 10,17 3,10"
      fill={color}
      opacity={0.8}
      stroke={color}
      strokeWidth={1}
    />
  ),
  LANCER: ({ color }) => (
    <>
      <polygon points="10,2 13,14 10,12 7,14" fill={color} />
      <line
        x1="10"
        y1="12"
        x2="10"
        y2="19"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </>
  ),
  LIGHT_CAVALRY: ({ color }) => (
    <polygon
      points="3,10 11,6 11,8 17,8 17,12 11,12 11,14"
      fill={color}
      opacity={0.8}
      stroke={color}
      strokeWidth={0.5}
    />
  ),
  MARSHALL: ({ color }) => (
    <polygon
      points="10,2 12,8 18,8 13,12 15,18 10,14 5,18 7,12 2,8 8,8"
      fill={color}
      opacity={0.85}
      stroke={color}
      strokeWidth={0.5}
    />
  ),
  MERCENARY: ({ color }) => (
    <>
      <circle
        cx="10"
        cy="10"
        r="7"
        fill="none"
        stroke={color}
        strokeWidth={2}
      />
      <circle cx="10" cy="10" r="3" fill={color} />
    </>
  ),
  PIKEMAN: ({ color }) => (
    <polygon
      points="10,2 12.5,10 10,18 7.5,10"
      fill={color}
      stroke={color}
      strokeWidth={0.5}
    />
  ),
  ROYAL_GUARD: ({ color }) => (
    <>
      <path
        d="M5,4 L15,4 L15,12 Q10,19 5,12 Z"
        fill={color}
        opacity={0.55}
        stroke={color}
        strokeWidth={1}
      />
      <polygon points="10,7 12,10 10,13 8,10" fill={color} />
    </>
  ),
  SCOUT: ({ color }) => (
    <>
      <path
        d="M3,10 Q10,3 17,10 Q10,17 3,10 Z"
        fill={color}
        opacity={0.25}
        stroke={color}
        strokeWidth={1.5}
      />
      <circle cx="10" cy="10" r="3.5" fill={color} />
    </>
  ),
  SWORDSMAN: ({ color }) => (
    <>
      <line
        x1="4"
        y1="17"
        x2="15"
        y2="6"
        stroke={color}
        strokeWidth={3}
        strokeLinecap="round"
      />
      <line
        x1="4"
        y1="13"
        x2="9"
        y2="18"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </>
  ),
  WARRIOR_PRIEST: ({ color }) => (
    <>
      <circle
        cx="10"
        cy="10"
        r="7"
        fill="none"
        stroke={color}
        strokeWidth={1.5}
      />
      <line
        x1="10"
        y1="4"
        x2="10"
        y2="16"
        stroke={color}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <line
        x1="4"
        y1="10"
        x2="16"
        y2="10"
        stroke={color}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
    </>
  ),
  ROYAL: ({ color }) => (
    <>
      <path
        d="M3,16 L3,9 L7,13 L10,5 L13,13 L17,9 L17,16 Z"
        fill={color}
        opacity={0.8}
        stroke={color}
        strokeWidth={0.5}
      />
      <line
        x1="3"
        y1="16"
        x2="17"
        y2="16"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </>
  ),
};

const FALLBACK: SymbolRenderer = ({ color }) => (
  <circle cx="10" cy="10" r="6" fill={color} opacity={0.6} />
);

/** Renders unit as a circular coin disc inside an existing SVG at board hex center (cx, cy). */
export function WarchestUnitSymbolInBoard({
  unitType,
  owner,
  cx,
  cy,
}: {
  readonly unitType: string;
  readonly owner: PlayerId;
  readonly cx: number;
  readonly cy: number;
}) {
  const Render = SYMBOLS[unitType] ?? FALLBACK;
  const fill = PLAYER_COLORS[owner];
  const border = PLAYER_DARK_COLORS[owner];
  return (
    <g transform={`translate(${cx - 10}, ${cy - 10})`}>
      <circle cx="10" cy="10" r="17" fill="var(--cn-warchest-coin-rim)" />
      <circle
        cx="10"
        cy="10"
        r="15"
        fill={fill}
        stroke={border}
        strokeWidth="0.75"
      />
      <Render color="rgba(255,255,255,0.92)" />
    </g>
  );
}

/** Standalone SVG icon for use in zone panel cards. */
export function WarchestUnitSymbolIcon({
  unitType,
  owner,
  size = 20,
  symbolColor,
}: {
  readonly unitType: string;
  readonly owner: PlayerId;
  readonly size?: number;
  readonly symbolColor?: string;
}) {
  const Render = SYMBOLS[unitType] ?? FALLBACK;
  const color = symbolColor ?? PLAYER_COLORS[owner];
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <Render color={color} />
    </svg>
  );
}
